import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '@monksflow/monks-ui';
import { useNavigate, useLocation } from 'react-router';
import { WizardLayout } from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { readFocusGroupsOrigin, withFocusGroupsOrigin } from '@/shared/utils/focusGroupsNavigation';
import { usePersonas } from '@/entities/persona';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import {
  useFocusGroupFormStore,
  selectIsSetupValid,
  selectIsReviewValid,
} from '../../stores/focusGroupFormStore';
import { useFocusGroupRunStore } from '../../stores/focusGroupRunStore';
import { useGenerateQuestions } from '../../api/useGenerateQuestions.mutation';
import { useRunFocusGroup } from '../../api/useRunFocusGroup';
import { SetupStep } from '../SetupStep';
import { ReviewQuestionsStep, GeneratingQuestionsLoading } from '../ReviewQuestionsStep';
import { RunStep } from '../RunStep';
import { FocusGroupPreview } from '../FocusGroupPreview';
import { buildStepLabels } from '../../constants';
import { useTranslation } from 'react-i18next';

export const NewFocusGroupLayout = () => {
  const { t } = useTranslation('audiences');

  const navigate = useNavigate();
  const { state } = useLocation() as { state: unknown };
  // Preserve the section entry origin so the back button keeps resolving to it.
  const origin = readFocusGroupsOrigin(state);
  const workspaceId = useCurrentWorkspaceId();
  const { data: personas } = usePersonas(workspaceId);
  const activeStep = useFocusGroupFormStore((s) => s.activeStep);
  const setActiveStep = useFocusGroupFormStore((s) => s.setActiveStep);
  const title = useFocusGroupFormStore((s) => s.title);
  const topic = useFocusGroupFormStore((s) => s.topic);
  const brandName = useFocusGroupFormStore((s) => s.brandName);
  const analysisId = useFocusGroupFormStore((s) => s.analysisId);
  const selectedPersonaIds = useFocusGroupFormStore((s) => s.selectedPersonaIds);
  const generatedQuestions = useFocusGroupFormStore((s) => s.generatedQuestions);
  const setGeneratedQuestions = useFocusGroupFormStore((s) => s.setGeneratedQuestions);
  const isSetupValid = useFocusGroupFormStore(selectIsSetupValid);
  const isReviewValid = useFocusGroupFormStore(selectIsReviewValid);
  const isRunning = useFocusGroupRunStore((s) => s.isRunning);
  const reportStatus = useFocusGroupRunStore((s) => s.reportStatus);
  const focusGroupId = useFocusGroupRunStore((s) => s.focusGroupId);
  const [isConfigPanelOpen, setIsConfigPanelOpen] = useState(true);

  // Track whether a run actually started during this mount. Re-opening the page
  // from a completed report can mount us with the previous run's
  // reportStatus='done'/focusGroupId still in the shared store (the parent reset
  // effect runs after our effects), so we must only auto-redirect for a run
  // launched here — not for a stale completed value left behind.
  const hasStartedRunRef = useRef(false);
  if (isRunning) {
    hasStartedRunRef.current = true;
  }

  // Once the run finishes, go straight to the report instead of showing an
  // intermediate "Your report is ready / View Report" step.
  useEffect(() => {
    if (hasStartedRunRef.current && reportStatus === 'done' && focusGroupId) {
      void navigate(ROUTES.TOOLS_FOCUS_GROUP_REPORT(focusGroupId), {
        replace: true,
        ...withFocusGroupsOrigin(origin),
      });
    }
  }, [reportStatus, focusGroupId, navigate, origin]);

  const selectedPersonas = useMemo(
    () => (personas ?? []).filter((p) => selectedPersonaIds.includes(p.id)),
    [personas, selectedPersonaIds],
  );

  const generateQuestions = useGenerateQuestions();
  const { run, cancel } = useRunFocusGroup();

  const canProceed = activeStep === 0 ? isSetupValid : activeStep === 1 ? isReviewValid : false;
  const isGenerating = activeStep === 1 && generateQuestions.isPending;

  const handleBack = () => {
    if (activeStep === 0) {
      void navigate(ROUTES.TOOLS_FOCUS_GROUPS, withFocusGroupsOrigin(origin));
    } else if (activeStep === 1) {
      setActiveStep(0);
    }
  };

  const cancelFocusGroup = () => {
    cancel();
    void navigate(ROUTES.TOOLS_FOCUS_GROUPS, withFocusGroupsOrigin(origin));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep(1);
      generateQuestions.mutate(
        { topic, brandName, analysisId: analysisId ?? undefined },
        {
          onSuccess: (data) => {
            setGeneratedQuestions(data.questions);
          },
        },
      );
    } else if (activeStep === 1) {
      const personaNames: Record<string, string> = {};
      for (const p of selectedPersonas) {
        personaNames[p.id] =
          p.personaTitle ?? p.segmentName ?? t('focusGroup.useRunFocusGroup.participant');
      }

      useFocusGroupRunStore.getState().initRun(selectedPersonaIds, personaNames);
      setActiveStep(2);

      void run({
        title,
        topic,
        brandName,
        analysisId: analysisId ?? undefined,
        personaIds: selectedPersonaIds,
        transformedQuestions: generatedQuestions.map((q) => q.text),
      });
    }
  };

  const handleRetry = () => {
    generateQuestions.mutate(
      { topic, brandName, analysisId: analysisId ?? undefined },
      {
        onSuccess: (data) => {
          setGeneratedQuestions(data.questions);
          setActiveStep(1);
        },
      },
    );
  };

  // single call site each, pure branch dispatch on `activeStep` — inlined
  // rather than extracted into `render*`-style functions with large closures.
  let stepContent: ReactNode;
  if (activeStep === 0) {
    stepContent = <SetupStep />;
  } else if (activeStep === 2) {
    stepContent = <RunStep />;
  } else if (isGenerating) {
    stepContent = <GeneratingQuestionsLoading />;
  } else if (generateQuestions.isError) {
    stepContent = (
      <div className="flex flex-col items-center justify-center flex-1 gap-4 py-12">
        <p className="text-base text-destructive">
          {t('focusGroup.newFocusGroupLayout.failedToGenerateQuestions')}
        </p>
        <Button variant="outline" onClick={handleRetry}>
          {t('focusGroup.newFocusGroupLayout.retry')}
        </Button>
      </div>
    );
  } else {
    stepContent = <ReviewQuestionsStep />;
  }

  let actions: ReactNode;
  if (activeStep === 2) {
    actions = (
      <div className="flex justify-center w-full mt-auto">
        <Button variant="outline" onClick={cancelFocusGroup}>
          {t('focusGroup.newFocusGroupLayout.cancel')}
        </Button>
      </div>
    );
  } else if (activeStep === 1 && isGenerating) {
    actions = (
      <div className="flex justify-center w-full mt-auto">
        <Button variant="outline" onClick={handleBack}>
          {t('focusGroup.newFocusGroupLayout.cancel')}
        </Button>
      </div>
    );
  } else if (activeStep === 1 && generateQuestions.isError) {
    actions = null;
  } else {
    actions = (
      <div className="flex justify-between items-center w-full mt-auto">
        <Button variant="outline" onClick={handleBack}>
          {t('focusGroup.newFocusGroupLayout.back')}
        </Button>
        <Button
          variant={canProceed ? 'default' : 'outline'}
          disabled={!canProceed}
          onClick={handleNext}
        >
          {activeStep === 1
            ? t('focusGroup.newFocusGroupLayout.runFocusGroup')
            : t('focusGroup.newFocusGroupLayout.next')}
        </Button>
      </div>
    );
  }

  return (
    <WizardLayout
      configTitle={t('focusGroup.newFocusGroupLayout.focusGroupConfiguration')}
      steps={buildStepLabels(t)}
      activeStep={activeStep}
      showConfigPanel={isConfigPanelOpen}
      onBack={isRunning ? cancelFocusGroup : handleBack}
      configContent={
        <div className="flex flex-col flex-1 w-full max-w-[650px] items-center gap-12 min-h-0">
          {stepContent}
          {actions}
        </div>
      }
    >
      <FocusGroupPreview
        isConfigPanelOpen={isConfigPanelOpen}
        onToggleConfigPanel={() => setIsConfigPanelOpen((open) => !open)}
      />
    </WizardLayout>
  );
};
